import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { IoEye } from "react-icons/io5";
import ProductModal from "../../../Components/Modals/Products/AddProducts";
import RecordPosition from "../../../Components/Modals/RecordPosition/RecordPosition";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { useProducts } from "./useHook";
import { FaTrash } from "react-icons/fa";
import Loader from "../../../Utils/loader";
import { FiFilter, FiPlus, FiRotateCcw, FiSearch } from "react-icons/fi";
import { MdDelete } from "react-icons/md";

function Products() {
  const {
    getCategories,
    getProductsWithPagination,
    updateProductStatus,
    deleteProduct,
    addProduct,
    getBrands,
    updateProductPosition,
  } = useProducts();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [productToEdit, setProductToEdit] = useState(null);
  const [brandData, setBrandData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedProductForPosition, setSelectedProductForPosition] =
    useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  // Get values from URL params or use defaults
  const offset = parseInt(searchParams.get("page") || "0") * limit;
  const searchQuery = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "";
  const categoryNameQuery = searchParams.get("categoryName") || "";

  // Local state for input fields
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [statusInput, setStatusInput] = useState(statusFilter);
  const [categoryNameInput, setCategoryNameInput] = useState(categoryNameQuery);

  const navigate = useNavigate();

  const fetchProducts = async (
    currentOffset,
    showLoader = true,
    currentSearchQuery,
    currentStatusFilter,
    currentCategoryName,
    currentLimit = limit,
  ) => {
    if (showLoader) {
      setIsLoading(true);
    }
    try {
      await getProductsWithPagination(
        setProducts,
        setTotal,
        currentOffset,
        currentLimit,
        currentSearchQuery,
        currentStatusFilter,
        currentCategoryName,
      );
    } catch (error) {
      console.error("Error fetching products:", error);
      Swal.fire("Error", "Failed to fetch products", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch (categories and brands)
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([
          getCategories(setCategories),
          getBrands(setBrandData),
        ]);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchInitialData();
  }, []);

  // Fetch products when pagination or filters change
  useEffect(() => {
    const fetchProductData = async () => {
      setIsLoading(true);
      try {
        await getProductsWithPagination(
          setProducts,
          setTotal,
          offset,
          limit,
          searchQuery,
          statusFilter,
          categoryNameQuery,
        );
      } catch (error) {
        console.error("Error fetching products:", error);
        Swal.fire("Error", "Failed to fetch products", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductData();
  }, [offset, searchQuery, statusFilter, categoryNameQuery, limit]);

  // Update input fields when URL params change
  useEffect(() => {
    setSearchInput(searchQuery);
    setStatusInput(statusFilter);
    setCategoryNameInput(categoryNameQuery);
  }, [searchQuery, statusFilter, categoryNameQuery]);

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      // Update URL params
      setSearchParams({
        page: "0",
        search: searchInput,
        status: statusInput,
        categoryName: categoryNameInput,
      });
      await getProductsWithPagination(
        setProducts,
        setTotal,
        0,
        limit,
        searchInput,
        statusInput,
        categoryNameInput,
      );
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    // Clear URL params and reset inputs
    setSearchParams({});
    setSearchInput("");
    setStatusInput("");
    setCategoryNameInput("");
    fetchProducts(0, false, "", "", "");
  };

  const handlePageChange = (newOffset) => {
    setIsLoading(true);
    const newPage = Math.floor(newOffset / limit);
    // Update URL params
    setSearchParams({
      page: newPage.toString(),
      search: searchQuery,
      status: statusFilter,
      categoryName: categoryNameQuery,
    });
    fetchProducts(
      newOffset,
      false,
      searchQuery,
      statusFilter,
      categoryNameQuery,
    );
  };

  const handleLimitChange = (newLimit, newOffset) => {
    setLimit(newLimit);
    setIsLoading(true);
    const newPage = Math.floor(newOffset / newLimit);
    // Update URL params and reset to first page
    setSearchParams({
      page: newPage.toString(),
      search: searchQuery,
      status: statusFilter,
      categoryName: categoryNameQuery,
    });
    fetchProducts(
      newOffset,
      false,
      searchQuery,
      statusFilter,
      categoryNameQuery,
      newLimit,
    );
  };

  const handleAddProduct = async (formData) => {
    try {
      setIsLoading(true);
      const response = await addProduct(formData);

      if (response) {
        Swal.fire({
          title: "Success!",
          text: "Product has been added successfully.",
          icon: "success",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: false,
          position: "center",
        });

        await new Promise((resolve) => setTimeout(resolve, 500));

        // Reset to first page after adding product
        setSearchParams({
          page: "0",
          search: searchQuery,
          status: statusFilter,
          categoryName: categoryNameQuery,
        });
        await fetchProducts(
          0,
          false,
          searchQuery,
          statusFilter,
          categoryNameQuery,
        );
        setShowModal(false);
      }
    } catch (error) {
      console.error("Error adding product:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to add product. Please try again.",
        icon: "error",
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewPage = (id) => {
    navigate("/products/view", {
      state: {
        id,
        returnUrl: window.location.pathname + window.location.search,
      },
    });
  };

  const handleOnSuccess = async () => {
    await fetchProducts(
      offset,
      false,
      searchQuery,
      statusFilter,
      categoryNameQuery,
    );
  };

  const handlePositionClick = (product) => {
    setSelectedProductForPosition({
      recordId: product.id,
      currentPosition: product.position ?? 0,
    });
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (recordId, newPosition) => {
    await updateProductPosition(recordId, newPosition);
    await fetchProducts(
      offset,
      false,
      searchQuery,
      statusFilter,
      categoryNameQuery,
    );
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedProductForPosition(null);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const response = await updateProductStatus(id, newStatus);

      if (response) {
        setProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.id === id ? { ...product, status: newStatus } : product,
          ),
        );

        Swal.fire({
          title: "Success",
          text: `Product status has been ${
            newStatus ? "activated" : "deactivated"
          } successfully!`,
          icon: "success",
          timer: 1000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.message || "Failed to update product status.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        const response = await deleteProduct(id);
        if (response) {
          await fetchProducts(
            offset,
            false,
            searchQuery,
            statusFilter,
            categoryNameQuery,
          );

          Swal.fire({
            title: "Deleted!",
            text: "The product has been deleted successfully.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
        }
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "Failed to delete the product. Please try again.",
        icon: "error",
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  if (isLoading) {
    return <Loader message="Loading products..." />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Products</h2>

        <button
          onClick={() => {
            setProductToEdit(null);
            setShowModal(true);
          }}
          className="btn btn-primary h-[44px]"
        >
          <FiPlus className="text-white" />
          Add Product
        </button>
      </div>

      <ProductModal
        categories={categories}
        brands={brandData}
        showModal={showModal}
        setShowModal={setShowModal}
        addProduct={handleAddProduct}
        productToEdit={productToEdit}
        onSuccess={handleOnSuccess}
      />

      {isPositionModalOpen && selectedProductForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={selectedProductForPosition.currentPosition}
          recordId={selectedProductForPosition.recordId}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Product"
        />
      )}

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by category name"
            value={categoryNameInput}
            onChange={(e) => setCategoryNameInput(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>

        <select
          className="status-select"
          value={statusInput}
          onChange={(e) => setStatusInput(e.target.value)}
        >
          <option value="" className="opacity-25">
            Select Status
          </option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Category</th>
                <th className="table-th">Name</th>
                <th className="table-th">Price</th>
                <th className="table-th">Status</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products?.filter(Boolean).length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-10 text-gray-500 text-lg font-medium"
                  >
                    No data found
                  </td>
                </tr>
              ) : (
                products?.filter(Boolean).map((product, index) => (
                  <tr
                    key={product.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">
                      <button
                        type="button"
                        onClick={() => handlePositionClick(product)}
                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                      >
                        {product.position}
                      </button>
                    </td>
                    <td className="table-td">
                      {product?.category?.name || "N/A"}
                    </td>
                    <td className="table-td">{product?.name}</td>
                    <td className="table-td">
                      {product?.currency} {product?.price}
                    </td>
                    <td className="table-td">
                      <div
                        className={`status-toggle ${
                          product?.status ? "active" : "bg-[#cccccc]"
                        }`}
                        onClick={() =>
                          handleToggleStatus(product.id, product.status)
                        }
                      >
                        <div className="status-toggle-handle"></div>
                      </div>
                    </td>

                    <td className="table-td flex space-x-2">
                      <button
                        className="btn btn-view "
                        onClick={() => handleViewPage(product.id)}
                      >
                        <IoEye className="text-white" /> View
                      </button>

                      <button
                        className="btn btn-delete text-white"
                        onClick={() => handleDelete(product.id)}
                      >
                        <MdDelete className="text-white" /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {total > 10 && (
        <div className="mt-6">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            className="bg-white"
            showItemsPerPage={true}
          />
        </div>
      )}
    </div>
  );
}

export default Products;
